import React, { useCallback, useContext, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { ActionSubcategory } from "../../backend/actions/Action";
import {
  getActionsForAreaByCategory,
  getActionsForAreaBySubcategory,
} from "../../backend/actions/Actions";
import { PlayerContext } from "../../backend/PlayerContext";
import { clone } from "../../utils/CoreUtils";
import { ActionContainer } from "./ActionContainer";
import CategoryContainer from "./CategoryContainer";
import Divider from "./Divider";
import { UIContext, useUIStateFromContext } from "./UIContext";

type ActionsContainerProps = {
  area: string;
  useSubcategories?: boolean;
  showAddToQuickbar?: boolean;
};

function ActionCategoryContainer(
  props: ActionsContainerProps & {
    category: string;
    useSubcategories: boolean;
    expandedActions: Record<string, boolean>;
    useButtonsWhenCollapsed: boolean;
    setActionCallback: (actionName: string) => void;
  },
) {
  const categories = props.useSubcategories
    ? getActionsForAreaBySubcategory(props.area)
    : getActionsForAreaByCategory(props.area);
  const category = props.category;
  const playerContext = useContext(PlayerContext);
  const actionNames = Object.keys(categories[category] || {});
  const visibleActionNames = actionNames.filter((actionName) => {
    return categories[category][actionName].isVisible(playerContext);
  });
  const visibleActionNamesStr = visibleActionNames.join(",");

  const actionContainers = useMemo(() => {
    return visibleActionNames.map((actionName) => {
      const action = categories[category][actionName];
      return (
        <ActionContainer
          key={actionName}
          action={action}
          collapsed={!props.expandedActions?.[actionName]}
          onPressExpand={props.setActionCallback.bind(null, actionName)}
          showAddToQuickbar={props.showAddToQuickbar}
          showCompact={!props.useButtonsWhenCollapsed}
        />
      );
    });
  }, [
    visibleActionNamesStr,
    category,
    JSON.stringify(props.expandedActions),
    props.setActionCallback,
    props.showAddToQuickbar,
  ]);

  return useMemo(() => {
    if (visibleActionNames.length === 0) {
      return null;
    }
    return (
      <CategoryContainer title={category} key={category}>
        {props.useButtonsWhenCollapsed && (
          <View style={styles.underCategoryPadding} />
        )}
        <View key={visibleActionNames[0]}>{actionContainers[0]}</View>
        {actionContainers.slice(1).map((actionContainer, idx) => {
          return (
            <View key={visibleActionNames[idx]}>
              {!props.useButtonsWhenCollapsed && <Divider />}
              {actionContainer}
            </View>
          );
        })}
      </CategoryContainer>
    );
  }, [visibleActionNamesStr, actionContainers, category]);
}

export default function ActionsContainer(props: ActionsContainerProps) {
  const uiContext = useContext(UIContext);
  const contextKey = "ActionsContainer." + props.area;
  const [expandedActions, setExpandedActions] = useUIStateFromContext<
    Record<string, boolean>
  >(uiContext, contextKey + ".ExpandedActions", {});
  const allowMaxOne = false;
  const setActionCallback = useCallback(
    (actionName: string) => {
      if (allowMaxOne) {
        if (expandedActions?.[actionName]) {
          setExpandedActions({});
        } else {
          const newExpandedActions: Record<string, boolean> = {};
          newExpandedActions[actionName] = true;
          setExpandedActions(newExpandedActions);
        }
      } else {
        expandedActions[actionName] = !expandedActions?.[actionName];
        setExpandedActions(clone(expandedActions));
      }
    },
    [expandedActions, allowMaxOne],
  );

  let actionCategories = [];

  if (!props.useSubcategories) {
    actionCategories = ["Actions", "Buildings"].map((category) => (
      <ActionCategoryContainer
        {...props}
        category={category}
        expandedActions={expandedActions}
        key={category}
        useSubcategories={false}
        useButtonsWhenCollapsed={true}
        setActionCallback={setActionCallback}
      />
    ));
  } else {
    actionCategories = Object.keys(ActionSubcategory).map((category) => (
      <ActionCategoryContainer
        {...props}
        category={category}
        expandedActions={expandedActions}
        key={category}
        useSubcategories={true}
        useButtonsWhenCollapsed={/*category === ActionSubcategory.Gather*/ true}
        setActionCallback={setActionCallback}
      />
    ));
  }

  return <View>{actionCategories}</View>;
}

const styles = StyleSheet.create({
  underCategoryPadding: {
    height: 8,
  },
});
